import styled, { keyframes } from 'styled-components';
import { colors } from '../../theme/colors';
import { radius } from '../../theme/radius';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'primary' | 'secondary' | 'white';

const sizeMap: Record<SpinnerSize, { size: number; border: number }> = {
  xs: { size: 16, border: 2 },
  sm: { size: 20, border: 2 },
  md: { size: 32, border: 3 },
  lg: { size: 48, border: 4 },
  xl: { size: 64, border: 5 },
};

const variantColors: Record<SpinnerVariant, string> = {
  primary: colors.primary[500],
  secondary: colors.secondary[500],
  white: colors.neutral.white,
};

export interface SpinnerProps {
  /** Size of the spinner */
  size?: SpinnerSize;
  /** Color variant */
  variant?: SpinnerVariant;
  /** Custom color (overrides variant) */
  color?: string;
  /** Additional class name */
  className?: string;
  /** Test id for testing */
  'data-testid'?: string;
}

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const StyledSpinner = styled.div<{
  $size: number;
  $border: number;
  $color: string;
}>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border: ${({ $border }) => $border}px solid ${colors.neutral[200]};
  border-top-color: ${({ $color }) => $color};
  border-radius: ${radius.circle};
  animation: ${spin} 0.8s linear infinite;
  display: inline-block;
`;

/**
 * Spinner component based on Libertify Design System
 *
 * A loading indicator for async operations.
 *
 * @example
 * ```tsx
 * import { Spinner } from '@libertify/ui';
 *
 * // Basic usage
 * <Spinner />
 *
 * // Different sizes
 * <Spinner size="lg" />
 *
 * // Different colors
 * <Spinner variant="secondary" />
 *
 * // Custom color
 * <Spinner color="#ff0000" />
 * ```
 */
export const Spinner = ({
  size = 'md',
  variant = 'primary',
  color,
  className,
  'data-testid': testId,
}: SpinnerProps) => {
  const { size: spinnerSize, border } = sizeMap[size];
  const spinnerColor = color || variantColors[variant];

  return (
    <StyledSpinner
      $size={spinnerSize}
      $border={border}
      $color={spinnerColor}
      className={className}
      data-testid={testId}
      role="status"
      aria-label="Loading"
    />
  );
};

export default Spinner;
